/* eslint-disable max-lines */
import { useTextToSpeech } from '@hakimo-ui/hakimo/data-access';
import { Button } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { FilePlus, MessageCircle, Mic, UserPlus } from 'lucide-react';
import { ComponentType, useMemo, useRef, useState } from 'react';
import {
  buildTalkdownText,
  STATIC_TEMPLATE,
  TalkdownMode,
} from '../audio-recorder/util';
import {
  FIELD_LABEL,
  STATIC_MESSAGE_BOX,
  TalkdownStep,
} from './sop-card-utils';
import {
  CustomMessage,
  DynamicMessage,
  TalkdownRecorder,
} from './TalkdownFields';

interface Props {
  onSend: (file: File) => void;
  isSending?: boolean;
  disabled?: boolean;
  trackingMeta?: Record<string, unknown>;
  step?: TalkdownStep;
}

export function TalkdownComposer(props: Props) {
  const { onSend, isSending, disabled, trackingMeta, step } = props;

  const sopScript = step?.text.trim() ?? '';
  const staticText =
    step?.mode === TalkdownMode.STATIC && sopScript
      ? sopScript
      : STATIC_TEMPLATE.text;

  const [mode, setMode] = useState(step?.mode ?? TalkdownMode.STATIC);
  const [describedPerson, setDescribedPerson] = useState('');
  const [typedMessage, setTypedMessage] = useState('');
  const [recordedFile, setRecordedFile] = useState<File>();
  const [error, setError] = useState('');
  const [isPreparingSend, setIsPreparingSend] = useState(false);

  const { isLoading, fetchAudio } = useTextToSpeech(trackingMeta);
  const cache = useRef<{ text: string; file: File; url: string }>();

  const isRecordMode = mode === TalkdownMode.RECORD;

  const text = useMemo(
    () => buildTalkdownText(mode, describedPerson, typedMessage, staticText),
    [mode, describedPerson, typedMessage, staticText]
  );

  const resolveAudio = async (): Promise<
    { file: File; url: string } | undefined
  > => {
    if (isRecordMode) {
      return undefined;
    }
    if (cache.current?.text === text) {
      return cache.current;
    }
    try {
      const { file, audioUrl } = await fetchAudio(text);
      cache.current = { text, file, url: audioUrl };
      setError('');
      return cache.current;
    } catch (err) {
      const detail = err instanceof Error ? err.message : '';
      setError(
        detail
          ? `Failed to generate the audio: ${detail}`
          : 'Failed to generate the audio. Please retry.'
      );
      return undefined;
    }
  };

  const onChangeMode = (next: TalkdownMode) => {
    setMode(next);
    setError('');
  };

  const onSendAudio = async () => {
    if (isRecordMode) {
      recordedFile && onSend(recordedFile);
      return;
    }
    if (!text) {
      setError('Add a message before sending it.');
      return;
    }
    setIsPreparingSend(true);
    try {
      const audio = await resolveAudio();
      audio && onSend(audio.file);
    } finally {
      setIsPreparingSend(false);
    }
  };

  const canSend = isRecordMode ? !!recordedFile : !!text;

  return (
    <div className="flex flex-col gap-[15px]">
      <p className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
        Send a message to the{' '}
        <span className="text-onlight-text-1 dark:text-ondark-text-1 font-semibold">
          person on camera.
        </span>
      </p>

      <TalkdownModeTiles mode={mode} onChange={onChangeMode} />

      {mode === TalkdownMode.STATIC && (
        <div className={STATIC_MESSAGE_BOX}>{staticText}</div>
      )}

      {mode === TalkdownMode.DYNAMIC && (
        <>
          {step?.mode === TalkdownMode.DYNAMIC && sopScript && (
            <div className="space-y-2">
              <div className={FIELD_LABEL}>SOP script for this talkdown</div>
              <div className={STATIC_MESSAGE_BOX}>{sopScript}</div>
            </div>
          )}
          <DynamicMessage
            value={describedPerson}
            onChange={setDescribedPerson}
            isLoading={isLoading}
            resolveAudio={resolveAudio}
            text={text}
          />
        </>
      )}

      {mode === TalkdownMode.CUSTOM && (
        <CustomMessage
          value={typedMessage}
          onChange={setTypedMessage}
          isLoading={isLoading}
          resolveAudio={resolveAudio}
          text={text}
        />
      )}

      {isRecordMode && (
        <TalkdownRecorder file={recordedFile} onRecorded={setRecordedFile} />
      )}

      {error && <p className="text-alert-500 text-xs">{error}</p>}

      <Button
        variant="primary"
        onClick={onSendAudio}
        loading={isSending || isPreparingSend}
        disabled={disabled || !canSend || isSending || isPreparingSend}
        className="w-full justify-center"
      >
        Send Audio
      </Button>
    </div>
  );
}

const MODES: {
  id: TalkdownMode;
  label: string;
  Icon: ComponentType<{ className?: string }>;
}[] = [
  { id: TalkdownMode.STATIC, label: 'Static', Icon: MessageCircle },
  { id: TalkdownMode.DYNAMIC, label: 'Dynamic', Icon: UserPlus },
  { id: TalkdownMode.CUSTOM, label: 'Custom', Icon: FilePlus },
  { id: TalkdownMode.RECORD, label: 'Record', Icon: Mic },
];

interface ModeTilesProps {
  mode: TalkdownMode;
  onChange: (mode: TalkdownMode) => void;
}

function TalkdownModeTiles({ mode, onChange }: ModeTilesProps) {
  return (
    <div
      role="group"
      aria-label="Talkdown message type"
      className="grid grid-cols-4 gap-2"
    >
      {MODES.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          aria-pressed={id === mode}
          title={label}
          onClick={() => onChange(id)}
          className={clsx(
            'flex flex-col items-center justify-center gap-2 rounded-lg px-2 py-4 outline outline-1 outline-offset-[-1px]',
            id === mode
              ? 'outline-primary-500 text-primary-600 dark:text-primary-400 dark:bg-primary-900/20 bg-primary-50'
              : 'text-onlight-text-3 outline-onlight-line-3 dark:text-ondark-text-2 dark:outline-ondark-line-1'
          )}
        >
          <Icon className="size-5" />
          <span className="text-center text-xs">{label}</span>
        </button>
      ))}
    </div>
  );
}

export default TalkdownComposer;
